import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LANGS } from "./i18n";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Retreats from "./pages/Retreats";
import PackageDetail from "./pages/PackageDetail";
import Login from "./pages/Login";
import Trips from "./pages/Trips";
import TripDetail from "./pages/TripDetail";
import BookingSuccess from "./pages/BookingSuccess";
import Policy from "./pages/Policy";
import RetreatExperiences from "./pages/RetreatExperiences";
import RokertStory from "./pages/RokertStory";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminPackages from "./pages/admin/AdminPackages";
import AdminChats from "./pages/admin/AdminChats";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminGuests from "./pages/admin/AdminGuests";
import Panel from "./pages/panel/Panel";

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback FIRST (synchronously, before protected routes run)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const pages = (key) => (
    <Route key={key} element={<Layout />}>
      <Route index element={<Landing />} />
      <Route path="retreats" element={<RetreatExperiences />} />
      <Route path="retreats/:slug" element={<PackageDetail />} />
      <Route path="experiences" element={<RetreatExperiences />} />
      <Route path="story" element={<RokertStory />} />
      <Route path="blog" element={<Blog />} />
      <Route path="blog/:slug" element={<BlogPost />} />
      <Route path="collection" element={<Retreats />} />
      <Route path="policy" element={<Policy />} />
      <Route path="login" element={<Login />} />
      <Route path="trips" element={<ProtectedRoute><Trips /></ProtectedRoute>} />
      <Route path="trips/:id" element={<ProtectedRoute><TripDetail /></ProtectedRoute>} />
      <Route path="booking/success" element={<ProtectedRoute><BookingSuccess /></ProtectedRoute>} />
    </Route>
  );
  return (
    <>
      <BrowserLangRedirect />
      <Routes>
        <Route path="/">
          {pages("root")}
        </Route>
        <Route path="/:lang">
          {pages("lang")}
        </Route>
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminBookings />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="guests" element={<AdminGuests />} />
          <Route path="chats" element={<AdminChats />} />
          <Route path="packages" element={<AdminPackages />} />
          <Route path="blog" element={<AdminBlog />} />
        </Route>
        <Route path="/panel" element={<Panel />} />
      </Routes>
    </>
  );
}

// One-time browser-language detection: redirect first-time visitors on the
// English root to their language (/sv, /de, /nl, /fr) if the browser matches.
function BrowserLangRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (localStorage.getItem("rr_lang_seen")) return;
    const seg = location.pathname.split("/")[1];
    if (LANGS.includes(seg)) { localStorage.setItem("rr_lang_seen", "1"); return; }
    if (["/admin", "/panel"].some((p) => location.pathname.startsWith(p))) return;
    const detected = (navigator.language || "en").slice(0, 2).toLowerCase();
    localStorage.setItem("rr_lang_seen", "1");
    if (["sv", "de", "nl", "fr"].includes(detected)) {
      navigate(`/${detected}${location.pathname === "/" ? "" : location.pathname}`, { replace: true });
    }
  }, []); // eslint-disable-line
  return null;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-center" theme="dark" />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
